import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link2, Loader2 } from "lucide-react";

import { toast } from "@/shared/hooks/use-toast";
import { Button } from "@/shared/ui/button";
import { CARD_WALL_RADIUS } from "@/shared/ui/cardWall";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/ui/form";
import { Input } from "@/shared/ui/input";
import { cn } from "@/shared/lib/utils";

import { websiteService, type DomainCard } from "../../api/websiteService";
import {
  APEX_HINT,
  domainSteps,
  isValidDomain,
  normalizeDomainInput,
} from "../../model/domainStatus";

const FORMAT_MESSAGE = "域名格式不对,例:www.example.com";

const schema = z.object({
  // 先剥协议 / 路径 / 末尾点、中文域名转 punycode、小写(`https://www.a.com/`、`www.a.com.` 都认成 www.a.com),
  // 再过与 api 同口径的格式闸(每段首尾不能是 `-`、顶级是字母或 xn--、不能是裸的 `com.cn`)。
  // (preprocess 的入参类型是 unknown,表单值就成了 unknown;用 transform + pipe 做同一件事,类型仍是 string。)
  domain: z
    .string()
    .transform(normalizeDomainInput)
    .pipe(z.string().refine(isValidDomain, FORMAT_MESSAGE)),
  // 备案号(选填,A.54):只展示不校验格式,去首尾空白后空串不发。
  icpNo: z.string().max(64, "备案号太长").optional(),
});
type Values = z.input<typeof schema>;
type Parsed = z.output<typeof schema>;

/**
 * 屏 5 还没有自有域名时的「填写域名」表单 → `POST /sites/{id}/domains {domain}`。
 *
 * ★ 前端先剥掉协议 / 路径 / 末尾点,再按 api 同一条规则挡一遍(归一化后比),api 仍会再归一化 / 校验:
 *   400 → 字段下直接显示 api 的话;409 分两种 —— 「域名 x 已被绑定」(全局唯一)与「发布服务器绑定域名失败:…」
 *   (网关错,api 已回滚本地行),标题按原话分开,不把网关失败说成「被别人绑了」。
 * ★ 写口不回桩:绑定失败就是失败,不在页面上画一张假的「待配置」卡。
 * ★ host 模式(A.54):多一格「备案号(选填)」→ `icpNo`;站点还没发布过(宿主上还没有这个站)时按钮禁用并说「先发布一次」
 *   (api 这时回 409「先发布一次再绑定域名」,不让用户撞上去)。
 */
export function BindDomainForm({
  siteId,
  simulated = false,
  showIcp = false,
  blockedReason = null,
  onBound,
}: {
  siteId: string;
  /** 模拟发布(A.38):四步的第 4 步不说「证书签发」。 */
  simulated?: boolean;
  /** host 模式:出「备案号(选填)」一格。 */
  showIcp?: boolean;
  /** 不能绑的原因(如「先发布一次」);非空时按钮禁用并把原因写在表单下。 */
  blockedReason?: string | null;
  onBound: (card: DomainCard) => void;
}) {
  const [submitting, setSubmitting] = useState(false);
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { domain: "", icpNo: "" },
  });

  const submit = async ({ domain, icpNo }: Parsed) => {
    if (blockedReason) return;
    const icp = showIcp ? icpNo?.trim() : "";
    setSubmitting(true);
    try {
      const card = await websiteService.bindDomain(
        siteId,
        icp ? { domain, icpNo: icp } : { domain }
      );
      toast({
        title: `已添加 ${card.domain}`,
        description: "按下面的步骤去 DNS 服务商那里加记录。",
      });
      onBound(card);
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      const status = (e as { httpStatus?: unknown } | null)?.httpStatus;
      if (status === 409) {
        toast({
          title: message.includes("已被绑定") ? "这个域名已被其他站点绑定" : "绑定失败",
          description: message,
          variant: "destructive",
        });
      } else if (status === 400) {
        form.setError("domain", { type: "server", message: message || FORMAT_MESSAGE });
      } else {
        toast({ title: "绑定失败", description: message, variant: "destructive" });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      aria-label="自有域名"
      className={cn("border border-border bg-card p-4 text-card-foreground", CARD_WALL_RADIUS)}
    >
      <h2 className="text-sm font-semibold text-foreground">自有域名</h2>
      <p className="mt-1 text-xs text-muted-foreground">
        还没有绑定自有域名。绑定后按四步走:{domainSteps(simulated).join(" → ")}。
      </p>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(submit)}
          noValidate
          className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-start"
        >
          <FormField
            control={form.control}
            name="domain"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormLabel>填写域名</FormLabel>
                <FormControl>
                  <Input
                    inputMode="url"
                    autoComplete="off"
                    spellCheck={false}
                    placeholder="www.example.com"
                    {...field}
                  />
                </FormControl>
                <FormDescription>{APEX_HINT}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          {showIcp && (
            <FormField
              control={form.control}
              name="icpNo"
              render={({ field }) => (
                <FormItem className="sm:w-56">
                  <FormLabel>备案号(选填)</FormLabel>
                  <FormControl>
                    <Input autoComplete="off" placeholder="浙ICP备…号" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
          <Button
            type="submit"
            className="sm:mt-8"
            disabled={submitting || !!blockedReason}
            title={blockedReason ?? undefined}
          >
            {submitting ? <Loader2 className="animate-spin" /> : <Link2 />}
            绑定域名
          </Button>
        </form>
      </Form>
      {blockedReason && (
        <p role="note" className="mt-2 text-xs text-muted-foreground">
          {blockedReason}
        </p>
      )}
    </section>
  );
}
